/**
 * HeritageWood — Gravură laser: add-to-cart cu serviciu plătit.
 * Când caseta e bifatǎ, adaugă produsul + varianta de gravură în același request,
 * apoi declanșează evenimentele native Horizon ca să se deschidă sertarul coșului.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const FORM_ID = 'hw-product-form';

function formatRon(cents) {
  const ron = Number(cents) / 100;
  if (!Number.isFinite(ron)) return '0';
  if (Number.isInteger(ron)) return String(ron);
  return ron.toFixed(2).replace('.', ',');
}

function getSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
}

function cartAddUrl() {
  return window.Theme?.routes?.cart_add_url || '/cart/add.js';
}

function openCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
  }
}

function isEngravingOn() {
  const toggle = document.getElementById('hwEngravingToggle');
  return Boolean(toggle && toggle.checked);
}

function setEngravingUi(enabled) {
  const checkbox = document.getElementById('hwPersonalizationCheckbox');
  const form = document.getElementById('hwPersonalizationForm');
  const input = document.getElementById('hwGravuraText');

  checkbox?.classList.toggle('checked', enabled);
  form?.classList.toggle('active', enabled);

  if (input) {
    input.disabled = !enabled;
    if (enabled) {
      input.removeAttribute('disabled');
    }
  }

  updateDisplayedPrice(enabled);
}

function updateDisplayedPrice(enabled) {
  const box = document.getElementById('hwProductPersonalization');
  const extra = Number(box?.dataset.engravingCents || 0);
  const priceBlock = document.querySelector('.hw-product-price-block');
  const priceRoot = document.querySelector('.hw-product-price-block .hw-price');
  if (!priceRoot) return;

  const amountEl = priceRoot.querySelector('span:not(.hw-price-currency)');
  if (!amountEl) return;

  const base = Number(priceBlock?.dataset.hwBaseCents || priceRoot.dataset.hwBaseCents || 0);
  if (!base) return;

  const total = enabled ? base + extra : base;
  amountEl.textContent = formatRon(total);
}

function updateCharCount(input) {
  const count = document.getElementById('hwCharCount');
  if (!count || !input) return;
  count.textContent = `${input.value.length} / 100`;
}

function setExampleText(text) {
  const input = document.getElementById('hwGravuraText');
  const toggle = document.getElementById('hwEngravingToggle');
  if (!input) return;
  if (toggle && !toggle.checked) {
    toggle.checked = true;
    setEngravingUi(true);
  }
  input.disabled = false;
  input.value = text;
  updateCharCount(input);
  input.focus();
}

async function refreshCart() {
  const response = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
  });
  return response.json();
}

function dispatchCartError(target, error) {
  target.dispatchEvent(
    new CartErrorEvent({
      error: error?.message || error || 'Add to cart failed',
      code: 'INVALID',
      detail: { description: error?.description || error?.message },
    })
  );
}

async function addToCartWithOptionalEngraving(form, submitEvent) {
  const variantInput = form.querySelector('[name="id"]');
  const qtyInput = form.querySelector('[name="quantity"]');
  const productVariantId = Number(variantInput?.value);
  const quantity = Math.max(1, parseInt(qtyInput?.value, 10) || 1);

  if (!productVariantId) {
    return;
  }

  const box = document.getElementById('hwProductPersonalization');
  const engravingVariantId = Number(box?.dataset.engravingVariantId || 0);
  const productTitle = box?.dataset.productTitle || '';
  const messageInput = document.getElementById('hwGravuraText');
  const message = (messageInput?.value || '').trim();
  const engravingOn = isEngravingOn();

  if (engravingOn && !engravingVariantId) {
    window.alert(
      'Gravura nu poate fi adăugată în coș încă. Produsul-serviciu de gravură nu este configurat. Contactează magazinul sau debifează opțiunea.'
    );
    return;
  }

  const items = [
    {
      id: productVariantId,
      quantity,
      properties: engravingOn
        ? {
            'Gravură personalizată': message || 'Da',
          }
        : undefined,
    },
  ];

  if (engravingOn && engravingVariantId) {
    items.push({
      id: engravingVariantId,
      quantity,
      parent_id: productVariantId,
      properties: {
        Pentru: productTitle,
        'Mesaj gravură': message || '—',
      },
    });
  }

  if (!engravingOn) {
    delete items[0].properties;
  }

  const productForm = form.closest('product-form-component') || form;
  const sectionIds = getSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();

  productForm.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({
        merchandiseId: String(item.id),
        quantity: item.quantity,
      })),
      promise: deferred.promise,
    })
  );

  const submitButton = form.querySelector('[type="submit"]');
  const originalLabel = submitButton?.innerHTML;
  if (submitButton) {
    submitButton.disabled = true;
  }

  try {
    const payload = {
      items: items.map((item) => {
        const entry = { id: item.id, quantity: item.quantity };
        if (item.properties) entry.properties = item.properties;
        if (item.parent_id) entry.parent_id = item.parent_id;
        return entry;
      }),
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };

    const response = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (data.status) {
      const ajaxCart = await refreshCart().catch(() => ({ items: [] }));
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: true,
          items: ajaxCart.items,
          source: 'hw-engraving-cart',
        },
      });
      dispatchCartError(productForm, data);
      window.alert(data.description || data.message || 'Nu s-a putut adăuga în coș.');
      return;
    }

    const ajaxCart = await refreshCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'hw-engraving-cart',
        itemCount: quantity,
        sections: data.sections,
        didError: false,
      },
    });

    openCartDrawer();
  } catch (error) {
    deferred.reject(error);
    dispatchCartError(productForm, error);
    window.alert('Nu s-a putut adăuga în coș. Încearcă din nou.');
  } finally {
    if (submitButton) {
      submitButton.disabled = false;
      if (originalLabel) submitButton.innerHTML = originalLabel;
    }
  }
}

function bind() {
  const form = document.getElementById(FORM_ID);
  const toggle = document.getElementById('hwEngravingToggle');
  const input = document.getElementById('hwGravuraText');
  const box = document.getElementById('hwProductPersonalization');

  if (toggle) {
    toggle.addEventListener('change', () => setEngravingUi(toggle.checked));
    setEngravingUi(toggle.checked);
  }

  if (input) {
    input.addEventListener('input', () => updateCharCount(input));
  }

  box?.querySelectorAll('[data-hw-example]').forEach((button) => {
    button.addEventListener('click', () => setExampleText(button.getAttribute('data-hw-example') || ''));
  });

  window.hwTogglePersonalization = function () {
    if (!toggle) return;
    toggle.checked = !toggle.checked;
    setEngravingUi(toggle.checked);
  };

  window.hwUpdateCharCount = updateCharCount;
  window.hwSetText = setExampleText;

  if (!form || form.dataset.hwEngravingBound === 'true') return;
  form.dataset.hwEngravingBound = 'true';

  form.addEventListener(
    'submit',
    (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      addToCartWithOptionalEngraving(form, event);
    },
    true
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bind);
} else {
  bind();
}
